import { useEffect } from "react";
import {
  Box,
  Center,
  Text,
  Heading,
  VStack,
} from "@chakra-ui/react";
import twitter from "twitter-text";
import safeJsonStringify from 'safe-json-stringify';
import async from 'async';
import toast from "react-hot-toast";
import { CustomHead } from "components/custom-head";
import { firebaseAdmin } from "firebaseAdmin";
import { index, createSlug } from "controllers/seo";
import { Profile } from "components/public-profile/profile";
import { AccountInfoProps } from "components/public-profile/account-info";
import { SecondaryCta } from "components/public-profile/call-to-action";
import { getApiUrl } from "utils/ez4castConfig";
interface Props {
  account: AccountInfoProps;
  tweets?: any[];
  secondaryCta?: SecondaryCta;
  page?: number;
  hasMore?: boolean;
  query?: any
}

const Home = ({ account, tweets = [], secondaryCta, page = 1, hasMore = false, query }: Props) => {


  useEffect(() => {
    // verify
    if (query?.email && !query?.unsubscribe) {
      toast.promise(new Promise(async (resolve, reject) => {
        try {
          console.log("did i come here?")
          const response = await fetch("/api/email/verify", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ emailHash: query.email })
          })
          const data = await response.json();

          if (data.success) {
            resolve(data.message);
          } else {
            reject(data.message);
          }

        } catch (e) {
          reject(e)
        }
      }), {
        loading: "Verifying...",
        success: (data) => `${data}`,
        error: (data) => `${data}`
      })
    }

    // unsubscribe
    if (query?.email && query?.unsubscribe && query?.unsubscribe !== "all") {
      toast.promise(new Promise(async (resolve, reject) => {
        try {
          const response = await fetch("/api/email/unsubscribe", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ email: query.email, authorId: query.unsubscribe })
          })
          const data = await response.json();

          if (data.success) {
            resolve(data.message);
          } else {
            reject(data.message);
          }

        } catch (e) {
          reject(e);
        }
      }), {
        loading: "Unsubscribing...",
        success: (data) => `${data}`,
        error: (data) => `${data}`
      })
    }
  }, [])

  const title = "@" + account?.username + "'s Latest X Threads";
  const desc = "Discover the best content of " + account.name + " all packaged in one place. Subscribe to never miss the new ones.";
  const url = "https://tweethunter.io/thread-finder/" + account?.username;
  let image = "https://ondemand.bannerbear.com/simpleurl/xMwbGrdpEE5BnDX2lq/image";
  image += "/handle/text/" + encodeURIComponent("@" + account?.username);
  image += "/name/text/" + encodeURIComponent(account?.name);
  image += "/profile/image_url/" + encodeURIComponent(account?.image);

  return (
    <Center w="100%" mt="10" p={{ base: 8, md: 20 }}>
      <CustomHead
        title={title}
        desc={desc}
        url={url}
        image={image}
      />
      <VStack alignItems="flex-start" spacing="16">
        <Box>
          <Heading fontSize="2xl" as="h1">{title}</Heading>
          <Text as="h2" mt="2" color="gray.500">Read threads by <b>{account?.name}</b> and get notified about future ones.</Text>
        </Box>
        <Profile
          account={account}
          tweets={tweets}
          secondaryCta={secondaryCta}
          page={page}
          hasMore={hasMore}
        />
      </VStack>
    </Center>
  )
};

export async function getServerSideProps({ params, res, query }) {

  let page = (query?.page ? parseInt(query?.page) : 1) ?? 1;
  let limit = (query?.limit ? parseInt(query?.lmit) : 10) ?? 10;
  let twUserName = params.twUserName;
  let props = { ...params, account: {}, page };

  if (twUserName && twUserName !== "requestProvider.js.map") {
    props = await getProfile(twUserName, page, limit);

    if (props?.account?.username && props?.account?.followers_count > 10000) {
      index(props.account.username, "https://tweethunter.io/thread-finder/" + props.account.username, "profile", { twUserName: props.account.username });
    }
  }

  if (props?.account?.username) {
    res.setHeader('Cache-Control', 'public, max-age=60, stale-while-revalidate=604800'); // 1min - 7 days
  }

  props = { ...props, query };

  const stringifiedData = safeJsonStringify(props);
  props = JSON.parse(stringifiedData);

  return {
    props: props,
  };
}

export default Home;

async function getProfile(username: string, page: number, limit = 10) {
  console.log('getProfile:');

  const db = firebaseAdmin.firestore();
  const accountDocs = await db
    .collectionGroup("accounts")
    .where("twUserName", "==", username)
    .get();

  // Is a TH user
  if (accountDocs.size) {
    const acc = accountDocs.docs[0].data();
    const account = {
      id_str: acc?.idAccount,
      name: acc?.name,
      username: acc?.twUserName,
      image: acc?.image,
      followers_count: acc?.followers_count,
      bioFormatted: acc?.bioFormatted,
      bio: acc?.bio
    }

    let secondaryCta;
    if (acc.addSecondaryCta && acc.ctaButtonLink && acc.ctaButtonText) {
      secondaryCta = {
        text: acc.ctaButtonText,
        link: acc.ctaButtonLink
      }
    }

    let tweets: any[] = [];
    let hasMore: boolean = false;

    const docsTweets = await db
      .collectionGroup("tweets")
      .where("idAccount", "==", acc.idAccount)
      .where("isGenerateBlog", "==", true)
      .where("status", "==", "sent")
      .orderBy("dateSent", "desc")
      .limit(limit * page)
      .get();

    if (docsTweets.docs?.length === page * limit) {
      hasMore = true;
    } else {
      hasMore = false;
    }

    let tempTweets = docsTweets.docs.map(doc => doc.data());
    tempTweets = tempTweets.slice(limit * (page - 1), limit * page);

    async.mapLimit(tempTweets, 10, async (tw) => {
      tw.dateCreated = tw.dateCreated?.toDate
        ? tw.dateCreated.toDate()
        : tw.dateCreated;
      tw.dateSent = tw.dateSent?.toDate ? tw.dateSent.toDate() : tw.dateCreated;

      if (tw.id_str)
        tw.text = tw.text.replace(
          /\[tweet\]/g,
          "https://twitter.com/" + tw.twUserName + "/status/" + tw.id_str
        );

      tw.firstTweet = tw.text?.split("\n\n\n")?.[0];

      if (!tw.slug) {
        await createSlug(tw);
      }

      tweets.push(tw);
    });

    return { page, account, secondaryCta, tweets, hasMore }
  }

  const { account, tweets, hasMore } = await getThreadsFromApi(username, page, limit);

  return { account, tweets, page, hasMore };
}

export async function getThreadsFromApi(
  username: string,
  page = 1,
  limit = 10
) {
  console.log("getFromApi: ", username);
  const data: any = { account: {}, tweets: [], hasMore: false };

  let url =
    `${getApiUrl("getThreadsHunter")}?twUserName=${username}&page=${page}&limit=${limit}`;
  console.log("getThreadsFromApi - url:", url);

  const result = await fetch(url);
  const json = await result.json();

  if (json?.success && json?.tweets && json?.account) {
    if (typeof json?.account?.entities === "string") {
      json.account.entities = JSON.parse(json?.account?.entities);
    }

    data.account = {
      id_str: json?.account?.id_str,
      name: json?.account.name,
      username: json?.account.screen_name,
      image: json?.account?.profile_image_url_https?.replace(
        "normal",
        "400x400"
      ),
      followers_count: json?.account.followers_count,
      bioFormatted: twitter.autoLink(json?.account.description, {
        urlEntities: json?.account?.entities?.description?.urls,
        usernameIncludeSymbol: true,
        targetBlank: true,
      }),
      bio: json?.account.description,
    };
    data.tweets = json.tweets;
    data.hasMore = json.hasMore ?? false;

    if (data.tweets?.length > 0) {
      async.mapLimit(data.tweets, 5, async (tw) => {
        tw.text = tw.full_text;
        tw.dateSent = tw.dateSent ?? tw.created_at;
        if (!tw.slug) {
          await createSlug(tw);
        }
      });
    }
  }

  return data;
}
